/**
 * Runtime data describing the set of query builder controls and the props each one receives.
 *
 * These live in `@react-querybuilder/core` (not `react-querybuilder`) so that non-React ports
 * can consume them without a React dependency.
 *
 * Nothing here can drift from the React implementation unnoticed:
 * `react-querybuilder`'s `types/controlPropKeys.test.ts` gates {@link controlKeys} against
 * `defaultControlElements` and `ControlElementsProp`, and every entry of
 * {@link controlPropKeys} against the corresponding props interface—both at runtime and at
 * compile time, in both directions.
 */

/**
 * The name of every query builder control, alphabetized.
 *
 * @group Props
 */
export const controlKeys = [
  'actionElement',
  'addGroupAction',
  'addRuleAction',
  'cloneGroupAction',
  'cloneRuleAction',
  'combinatorSelector',
  'dragHandle',
  'fieldSelector',
  'inlineCombinator',
  'lockGroupAction',
  'lockRuleAction',
  'matchModeEditor',
  'muteGroupAction',
  'muteRuleAction',
  'notToggle',
  'operatorSelector',
  'removeGroupAction',
  'removeRuleAction',
  'rule',
  'ruleGroup',
  'ruleGroupBodyElements',
  'ruleGroupHeaderElements',
  'shiftActions',
  'undoRedoActions',
  'valueEditor',
  'valueSelector',
  'valueSourceSelector',
] as const;

/**
 * The name of a query builder control.
 *
 * @group Props
 */
export type ControlKey = (typeof controlKeys)[number];

/**
 * The full set of prop names each default control receives. Ports can use this to declare
 * every prop explicitly instead of relying on fall-through/attribute inheritance.
 *
 * Each array is the complete, alphabetized key set of the control's props interface in
 * `react-querybuilder`. The lists are written out in full rather than composed from shared
 * bases because `--isolatedDeclarations` cannot infer array types built with spreads. A
 * compile-time gate in `react-querybuilder` (`types/controlPropKeys.test.ts`) fails the build
 * if any list drifts from its interface in either direction.
 *
 * @group Props
 */
export const controlPropKeys = {
  actionElement: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  addGroupAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  addRuleAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  cloneGroupAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  cloneRuleAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  combinatorSelector: [
    'className',
    'context',
    'disabled',
    'handleOnChange',
    'level',
    'options',
    'path',
    'ruleGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
    'value',
  ],
  dragHandle: [
    'className',
    'context',
    'disabled',
    'dragHandleAttributes',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  fieldSelector: [
    'className',
    'context',
    'disabled',
    'handleOnChange',
    'level',
    'operator',
    'options',
    'path',
    'rule',
    'schema',
    'testID',
    'title',
    'validation',
    'value',
  ],
  inlineCombinator: [
    'className',
    'component',
    'context',
    'disabled',
    'handleOnChange',
    'level',
    'options',
    'path',
    'ruleGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
    'value',
  ],
  lockGroupAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  lockRuleAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  matchModeEditor: [
    'className',
    'classNames',
    'context',
    'disabled',
    'field',
    'fieldData',
    'handleOnChange',
    'level',
    'match',
    'numericEditorComponent',
    'options',
    'path',
    'rule',
    'schema',
    'selectorComponent',
    'testID',
    'thresholdPlaceholder',
    'title',
    'validation',
    'value',
  ],
  muteGroupAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  muteRuleAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  notToggle: [
    'checked',
    'className',
    'context',
    'disabled',
    'handleOnChange',
    'label',
    'level',
    'path',
    'ruleGroup',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  operatorSelector: [
    'className',
    'context',
    'disabled',
    'field',
    'fieldData',
    'handleOnChange',
    'level',
    'options',
    'path',
    'rule',
    'schema',
    'testID',
    'title',
    'validation',
    'value',
  ],
  removeGroupAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  removeRuleAction: [
    'className',
    'context',
    'disabled',
    'disabledTranslation',
    'handleOnClick',
    'label',
    'level',
    'path',
    'ruleOrGroup',
    'rules',
    'schema',
    'testID',
    'title',
    'validation',
  ],
  rule: [
    'actions',
    'context',
    'disabled',
    'dndRef',
    'dragHandleAttributes',
    'dragMonitorId',
    'dragRef',
    'dropEffect',
    'dropMonitorId',
    'dropNotAllowed',
    'field',
    'groupItems',
    'id',
    'isDragging',
    'isOver',
    'operator',
    'parentDisabled',
    'parentMuted',
    'path',
    'rule',
    'schema',
    'shiftDownDisabled',
    'shiftUpDisabled',
    'translations',
    'value',
    'valueSource',
  ],
  ruleGroup: [
    'actions',
    'combinator',
    'context',
    'disabled',
    'dragHandleAttributes',
    'dragMonitorId',
    'dragRef',
    'dropEffect',
    'dropMonitorId',
    'dropNotAllowed',
    'dropRef',
    'groupItems',
    'id',
    'isDragging',
    'isOver',
    'not',
    'parentDisabled',
    'parentMuted',
    'path',
    'previewRef',
    'ruleGroup',
    'rules',
    'schema',
    'shiftDownDisabled',
    'shiftUpDisabled',
    'translations',
  ],
  ruleGroupBodyElements: [
    'accessibleDescription',
    'actions',
    'addGroup',
    'addRule',
    'classNames',
    'cloneGroup',
    'combinator',
    'context',
    'disabled',
    'dragHandleAttributes',
    'dragMonitorId',
    'dragRef',
    'dropEffect',
    'dropMonitorId',
    'dropNotAllowed',
    'dropRef',
    'groupItems',
    'id',
    'isDragging',
    'isOver',
    'muted',
    'not',
    'onCombinatorChange',
    'onGroupAdd',
    'onIndependentCombinatorChange',
    'onNotToggleChange',
    'outerClassName',
    'parentDisabled',
    'parentMuted',
    'path',
    'pathsMemo',
    'previewRef',
    'removeGroup',
    'ruleGroup',
    'rules',
    'schema',
    'shiftDownDisabled',
    'shiftGroupDown',
    'shiftGroupUp',
    'shiftUpDisabled',
    'toggleLockGroup',
    'toggleMuteGroup',
    'translations',
    'validationClassName',
    'validationResult',
  ],
  ruleGroupHeaderElements: [
    'accessibleDescription',
    'actions',
    'addGroup',
    'addRule',
    'classNames',
    'cloneGroup',
    'combinator',
    'context',
    'disabled',
    'dragHandleAttributes',
    'dragMonitorId',
    'dragRef',
    'dropEffect',
    'dropMonitorId',
    'dropNotAllowed',
    'dropRef',
    'groupItems',
    'id',
    'isDragging',
    'isOver',
    'muted',
    'not',
    'onCombinatorChange',
    'onGroupAdd',
    'onIndependentCombinatorChange',
    'onNotToggleChange',
    'outerClassName',
    'parentDisabled',
    'parentMuted',
    'path',
    'pathsMemo',
    'previewRef',
    'removeGroup',
    'ruleGroup',
    'rules',
    'schema',
    'shiftDownDisabled',
    'shiftGroupDown',
    'shiftGroupUp',
    'shiftUpDisabled',
    'toggleLockGroup',
    'toggleMuteGroup',
    'translations',
    'validationClassName',
    'validationResult',
  ],
  shiftActions: [
    'className',
    'context',
    'disabled',
    'labels',
    'level',
    'path',
    'ruleOrGroup',
    'schema',
    'shiftDown',
    'shiftDownDisabled',
    'shiftUp',
    'shiftUpDisabled',
    'testID',
    'title',
    'titles',
    'validation',
  ],
  undoRedoActions: [
    'className',
    'classNames',
    'context',
    'disabled',
    'labels',
    'level',
    'path',
    'ruleOrGroup',
    'schema',
    'testID',
    'title',
    'titles',
    'validation',
  ],
  valueEditor: [
    'className',
    'context',
    'disabled',
    'field',
    'fieldData',
    'handleOnChange',
    'inputType',
    'level',
    'listsAsArrays',
    'operator',
    'parseNumbers',
    'path',
    'rule',
    'schema',
    'selectorComponent',
    'separator',
    'skipHook',
    'testID',
    'title',
    'type',
    'validation',
    'value',
    'valueSource',
    'values',
  ],
  valueSelector: [
    'className',
    'context',
    'disabled',
    'handleOnChange',
    'level',
    'listsAsArrays',
    'multiple',
    'options',
    'path',
    'schema',
    'testID',
    'title',
    'validation',
    'value',
  ],
  valueSourceSelector: [
    'className',
    'context',
    'disabled',
    'field',
    'fieldData',
    'handleOnChange',
    'level',
    'options',
    'path',
    'rule',
    'schema',
    'testID',
    'title',
    'validation',
    'value',
  ],
} as const;

/**
 * Which bulk override, if any, applies to each control: `"action"` for controls that a supplied
 * `actionElement` replaces, `"selector"` for controls that a supplied `valueSelector` replaces,
 * and `null` for controls that neither replaces.
 *
 * `actionElement` and `valueSelector` are themselves `null`—they are the _source_ of a bulk
 * override, not a target of one.
 *
 * Replaces name-suffix sniffing (`key.endsWith('Action')`), which would silently misclassify a
 * future control named e.g. `pathSelector`, and which disagrees between implementations about
 * whether `shiftActions`/`undoRedoActions` (plural) match. They do not: neither receives the
 * `actionElement` bulk override.
 *
 * @group Props
 */
export const controlKind = {
  actionElement: null,
  addGroupAction: 'action',
  addRuleAction: 'action',
  cloneGroupAction: 'action',
  cloneRuleAction: 'action',
  combinatorSelector: 'selector',
  dragHandle: null,
  fieldSelector: 'selector',
  inlineCombinator: null,
  lockGroupAction: 'action',
  lockRuleAction: 'action',
  matchModeEditor: null,
  muteGroupAction: 'action',
  muteRuleAction: 'action',
  notToggle: null,
  operatorSelector: 'selector',
  removeGroupAction: 'action',
  removeRuleAction: 'action',
  rule: null,
  ruleGroup: null,
  ruleGroupBodyElements: null,
  ruleGroupHeaderElements: null,
  shiftActions: null,
  undoRedoActions: null,
  valueEditor: null,
  valueSelector: null,
  valueSourceSelector: 'selector',
} as const;
